import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import MapAddressPicker from '../installer/MapAddressPicker';
import { reverseGeocode } from '../../utils/reverseGeocode';
import { customerApi, uploadNationalId } from '../../utils/customerApi';
import { normalizeServiceabilityDecision } from '../../utils/serviceabilityDecision';
import '../../styles/CustomerPages.css';

const toFa = (s) => String(s)
  .replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])
  .replace(/[٠-٩]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d.charCodeAt(0) - 0x0660]);
const toEn = (s) => String(s)
  .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06F0))
  .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660));

const SERVICE_TYPES = [
  { v: 'ftth',     label: 'فیبر نوری خانگی (FTTH)' },
  { v: 'business', label: 'فیبر نوری تجاری/سازمانی' },
  { v: 'consult',  label: 'مطمئن نیستم، مشاوره می‌خواهم' },
];

const HANDOFFS = new Set(['definite_registration', 'pre_registration', 'manual_review_or_callback']);
const DECISION_TUPLE_FIELDS = [
  'decision',
  'policy_level',
  'evidence_ref',
  'data_version',
  'decided_at',
];

function decisionFromContext(body) {
  if (!body || !DECISION_TUPLE_FIELDS.every((field) => Object.hasOwn(body, field))) {
    return 'unknown';
  }
  return normalizeServiceabilityDecision(body.decision);
}

function completionCopy(decision) {
  const normalized = normalizeServiceabilityDecision(decision);
  if (normalized === 'serviceable') {
    return {
      title: 'ثبت‌نام شما انجام شد ✅',
      message: 'درخواست شما ثبت شد و پس از بررسی توسط کارشناسان، برای هماهنگی نصب با شما تماس می‌گیریم.',
    };
  }
  if (normalized === 'not_serviceable') {
    return {
      title: 'پیش‌ثبت‌نام شما ثبت شد ✅',
      message: 'منطقهٔ شما هنوز تحت پوشش نیست؛ به‌محض گسترش پوشش، با شما تماس می‌گیریم. ممنون از همراهی شما.',
    };
  }
  return {
    title: 'پیش‌ثبت‌نام شما ثبت شد ✅',
    message: 'وضعیت پوشش منطقهٔ شما هنوز قطعی نشده است؛ کارشناسان ما آن را بررسی می‌کنند و نتیجه را به شما اطلاع می‌دهیم.',
  };
}

export default function CustomerAuthPage() {
  const [params] = useSearchParams();
  const contextToken = params.get('token') || '';
  const [stage, setStage] = useState('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [phoneToken, setPhoneToken] = useState('');
  const [handoff, setHandoff] = useState('manual_review_or_callback');
  const [serviceabilityDecision, setServiceabilityDecision] = useState('unknown');
  const [form, setForm] = useState({ full_name: '', address_text: '', postal_code: '', service_type: 'ftth', loc: null });
  const [file, setFile] = useState(null);
  const [documentDeferred, setDocumentDeferred] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [addrLoading, setAddrLoading] = useState(false);

  // Pre-fill the address from the feasibility location so the customer does not
  // retype it. Fills only when empty (the setForm guard), so it never overwrites
  // anything typed. The readOnly map means loc is set once on mount, so this runs
  // at most once. Soft-fails to manual entry.
  const locLat = form.loc?.lat ?? null;
  const locLng = form.loc?.lng ?? null;
  useEffect(() => {
    if (locLat == null || locLng == null) return undefined;
    let cancelled = false;
    setAddrLoading(true);
    reverseGeocode(locLat, locLng)
      .then((addr) => { if (!cancelled && addr) setForm((f) => (f.address_text ? f : { ...f, address_text: toFa(addr) })); })
      .catch(() => { /* leave the field empty for manual entry on failure */ })
      .finally(() => { if (!cancelled) setAddrLoading(false); });
    return () => { cancelled = true; };
  }, [locLat, locLng]);

  useEffect(() => {
    setHandoff('manual_review_or_callback');
    setServiceabilityDecision('unknown');
    try {
      const body = JSON.parse(atob(contextToken.split('.')[0].replace(/-/g, '+').replace(/_/g, '/')));
      if (typeof body.lat === 'number' && typeof body.lng === 'number') {
        setForm((f) => ({ ...f, loc: { lat: body.lat, lng: body.lng } }));
      }
      if (HANDOFFS.has(body.handoff)) setHandoff(body.handoff);
      setServiceabilityDecision(decisionFromContext(body));
    } catch { /* token is opaque to the client; server verifies it */ }
  }, [contextToken]);

  const guard = (fn) => async () => { setBusy(true); setErr(null);
    try { await fn(); } catch (e) { setErr(e.message); } finally { setBusy(false); } };

  const sendOtp = guard(async () => {
    const p = toEn(phone);
    if (!/^09\d{9}$/.test(p)) throw new Error('شماره موبایل باید ۱۱ رقم و با ۰۹ شروع شود.');
    await customerApi.requestOtp(p); setStage('otp');
  });
  const verify = guard(async () => {
    const r = await customerApi.verifyOtp(toEn(phone), toEn(otp));
    setPhoneToken(r.phone_token); setStage('form');
  });
  const submit = guard(async () => {
    if (!form.full_name.trim()) throw new Error('نام و نام خانوادگی الزامی است.');
    if (!form.address_text.trim()) throw new Error('آدرس دقیق الزامی است.');
    if (!form.loc) throw new Error('لطفاً موقعیت را روی نقشه مشخص کنید.');
    if (!file && !documentDeferred) throw new Error('تصویر کارت ملی الزامی است.');
    const postal = toEn(form.postal_code);
    if (postal && !/^\d{10}$/.test(postal)) throw new Error('کد پستی باید ۱۰ رقم باشد.');
    let national_id_path = null;
    if (file) {
      try {
        national_id_path = await uploadNationalId(file, phoneToken, contextToken);
      } catch {
        setFile(null);
        setDocumentDeferred(true);
        throw new Error(`بارگذاری تصویر کارت ملی فعلاً در دسترس نیست. برای ادامه بدون فایل دوباره دکمه ${actionLabel} را بزنید؛ مدرک بعداً از شما درخواست می‌شود.`);
      }
    }
    await customerApi.register({
      context_token: contextToken, phone_token: phoneToken, phone: toEn(phone),
      full_name: form.full_name, address_text: form.address_text, postal_code: postal,
      service_type: form.service_type, national_id_path,
      lat: form.loc.lat, lng: form.loc.lng,
    });
    setStage('done');
  });

  const actionLabel = handoff === 'definite_registration'
    ? 'ثبت‌نام'
    : handoff === 'pre_registration' ? 'پیش‌ثبت‌نام' : 'درخواست بررسی';
  const doneCopy = completionCopy(serviceabilityDecision);

  return (
    <div className="cust">
      <div className="cust-shell">
        <div className="cust-card">
          {err && <div className="cust-alert" role="alert">{err}</div>}

          {stage === 'phone' && (<>
            <h1 className="cust-h1">{actionLabel} اینترنت فیبر نوری</h1>
            <p className="cust-lead">برای ادامه، شماره موبایل خود را وارد کنید.</p>
            <div className="cust-field">
              <label className="cust-label" htmlFor="phone">شماره موبایل</label>
              <input className="cust-input" id="phone" inputMode="numeric" value={toFa(phone)}
                onChange={(e) => setPhone(toEn(e.target.value))} placeholder="۰۹۱۲۳۴۵۶۷۸۹" />
            </div>
            <div className="cust-btnrow">
              <button className="cust-btn" disabled={busy} onClick={sendOtp}>{busy && <span className="cust-spin" />}دریافت کد</button>
            </div>
          </>)}

          {stage === 'otp' && (<>
            <h1 className="cust-h1">کد تایید</h1>
            <p className="cust-lead">کد ارسال‌شده به شماره {toFa(phone)} را وارد کنید.</p>
            <div className="cust-field">
              <label className="cust-label" htmlFor="otp">کد تایید</label>
              <input className="cust-input cust-otp" id="otp" inputMode="numeric" maxLength={4} value={toFa(otp)}
                onChange={(e) => setOtp(toEn(e.target.value))} placeholder="----" />
            </div>
            <div className="cust-btnrow">
              <button className="cust-btn cust-btn-ghost" disabled={busy} onClick={() => { setOtp(''); setStage('phone'); }}>بازگشت</button>
              <button className="cust-btn" disabled={busy} onClick={verify}>{busy && <span className="cust-spin" />}تایید</button>
            </div>
          </>)}

          {stage === 'form' && (<>
            <h1 className="cust-h1">{actionLabel} - اطلاعات</h1>
            <div className="cust-field">
              <label className="cust-label" htmlFor="fn">نام و نام خانوادگی</label>
              <input className="cust-input" id="fn" value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            </div>
            <div className="cust-field">
              <label className="cust-label">موقعیت محل نصب (بررسی‌شده در مرحلهٔ امکان‌سنجی)</label>
              <MapAddressPicker value={form.loc} onChange={(loc) => setForm({ ...form, loc })} height={260} readOnly />
              <p className="cust-hint">این همان موقعیتی است که پوشش برایش بررسی شد. برای تغییر موقعیت، به مرحلهٔ مشاوره/امکان‌سنجی برگردید.</p>
            </div>
            <div className="cust-field">
              <label className="cust-label" htmlFor="svc">نوع سرویس</label>
              <select className="cust-select" id="svc" value={form.service_type}
                onChange={(e) => setForm({ ...form, service_type: e.target.value })}>
                {SERVICE_TYPES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
            </div>
            <div className="cust-field">
              <label className="cust-label" htmlFor="addr">آدرس دقیق</label>
              <textarea className="cust-input" id="addr" rows={2} value={form.address_text}
                onChange={(e) => setForm({ ...form, address_text: toFa(e.target.value) })} />
              {addrLoading && <p className="cust-hint">در حال یافتن آدرس...</p>}
            </div>
            <div className="cust-field">
              <label className="cust-label" htmlFor="pc">کد پستی</label>
              <input className="cust-input" id="pc" inputMode="numeric" maxLength={10} value={toFa(form.postal_code)}
                onChange={(e) => setForm({ ...form, postal_code: toEn(e.target.value) })} placeholder="۱۰ رقم" />
            </div>
            <div className="cust-field">
              <label className="cust-label" htmlFor="nid">تصویر کارت ملی</label>
              <input className="cust-file" id="nid" type="file" accept="image/*,.pdf"
                onChange={(e) => { setFile(e.target.files[0] || null); setDocumentDeferred(false); }} />
            </div>
            <div className="cust-btnrow">
              <button className="cust-btn" disabled={busy} onClick={submit}>{busy && <span className="cust-spin" />}{actionLabel}</button>
            </div>
          </>)}

          {stage === 'done' && (<>
            <h1 className="cust-h1">{doneCopy.title}</h1>
            <p className="cust-lead">{doneCopy.message}</p>
          </>)}
        </div>
      </div>
    </div>
  );
}
